import { useEffect, useMemo, type FC, type ReactElement } from "react";
import { useLocation } from "react-router-dom";

import { keepActiveTab } from "@/features/appTabs/context/appTabsStore";
import {
  useRegisterActiveTab,
  type ActiveTabRegistration
} from "@/features/appTabs/context/ActiveTabContext";
import { caseTabId } from "@/features/appTabs/tabRoutes";

import { useCase } from "@/contexts/CaseContext";

import { isCaseUnavailable, NO_ACCESS_TITLE } from "../caseAvailability";
import { useIdeLayout } from "../layout/ideLayoutStore";
import { parseCasePath } from "../routes";
import { CaseDetailsPopover } from "./CaseDetailsPopover";
import { publishCaseDetails } from "./caseDetailsStore";
import { AutomationChip } from "./chips/AutomationChip";
import { isWorkspacePath } from "./caseHeaderModel";
import { CaseyToggle } from "./CaseyToggle";
import { FilesToggle } from "./FilesToggle";
import { useCaseDetails, type CaseDetails } from "./useCaseDetails";

/** Editing a case property keeps its tab (a preview tab stops being one). */
const keepOnEdit = (details: CaseDetails): CaseDetails => {
  const kept =
    <A extends unknown[]>(fn: (...args: A) => Promise<void>) =>
    (...args: A) => {
      keepActiveTab();
      return fn(...args);
    };
  return {
    ...details,
    setStatus: kept(details.setStatus),
    setFilingDeadline: kept(details.setFilingDeadline),
    setPetitioner: kept(details.setPetitioner),
    setAssignees: kept(details.setAssignees),
    setVisa: kept(details.setVisa)
  };
};

/**
 * The case's part of the app tab bar: the tab's name and avatar, the files
 * and Casey toggles, and the details popover (status, filing deadline, petitioner,
 * assignees) the active case tab opens. Renders nothing itself. On a case the
 * Case Assistant (IDE right pane) is the agent, so the bar's agent button
 * is hidden.
 */
export const CaseHeader: FC = () => {
  const location = useLocation();
  const details = useCaseDetails();
  const ide = useIdeLayout();
  const showIdeToggles = isWorkspacePath(location.pathname) && ide.mounted;
  // Deep-linked into a case: Back steps up to the client's cases, else Cases.
  const clientId = parseCasePath(location.pathname)?.ref.foreignNationalId;
  const parentTo = clientId ? `/clients/${clientId}?tab=cases` : "/cases";

  const {
    isLoading,
    clientName,
    workflow,
    clientInitials,
    clientAvatarUrl,
    clientSubtitle,
    foreignNationalId,
    individualId
  } = details;
  const { case: caseInfo, error: caseError } = useCase();
  const unavailable = !caseInfo && isCaseUnavailable(caseError);
  const meta = useMemo(
    () =>
      unavailable
        ? {
            title: NO_ACCESS_TITLE,
            workflow: "",
            initials: "",
            avatarUrl: "",
            clientId: "",
            subtitle: ""
          }
        : isLoading
        ? undefined
        : {
            title: clientName || "Unknown client",
            workflow: workflow || undefined,
            initials: clientInitials,
            avatarUrl: clientAvatarUrl,
            clientId: foreignNationalId ?? undefined,
            subtitle: clientSubtitle
          },
    [
      unavailable,
      isLoading,
      clientName,
      workflow,
      clientInitials,
      clientAvatarUrl,
      foreignNationalId,
      clientSubtitle
    ]
  );

  const reg = useMemo<ActiveTabRegistration | null>(
    () =>
      individualId
        ? {
            tabId: caseTabId(individualId),
            meta,
            left: showIdeToggles && !unavailable ? <FilesToggle /> : null,
            actions: unavailable ? null : (
              <AutomationChip individualId={individualId} />
            ),
            right: showIdeToggles && !unavailable ? <CaseyToggle /> : null,
            renderDetails:
              isLoading || unavailable
                ? undefined
                : (trigger: ReactElement) => (
                    <CaseDetailsPopover details={keepOnEdit(details)}>
                      {trigger}
                    </CaseDetailsPopover>
                  ),
            fallbackBackTo: parentTo
          }
        : null,
    [
      details,
      individualId,
      isLoading,
      meta,
      parentTo,
      showIdeToggles,
      unavailable
    ]
  );
  useRegisterActiveTab(reg);
  useEffect(() => {
    publishCaseDetails(isLoading ? null : keepOnEdit(details));
  }, [details, isLoading]);
  useEffect(() => () => publishCaseDetails(null), []);
  return null;
};
